// Shared by modals that trap Tab: the focusable selector and a visibility
// filter that also rejects visibility:hidden, `inert` and display:none subtrees.
export const FOCUSABLE =
  "button:not(:disabled), input:not(:disabled):not([type=hidden]), a[href], select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])";

// Older DOM typings lack visibilityProperty, so describe the call ourselves.
type VisibilityOpts = { visibilityProperty?: boolean };
type MaybeCheckVisibility = HTMLElement & {
  checkVisibility?: (o?: VisibilityOpts) => boolean;
};

export const isVisible = (el: HTMLElement): boolean => {
  if (el.closest("[inert]")) return false;
  const checkable = el as MaybeCheckVisibility;
  if (typeof checkable.checkVisibility === "function") {
    return checkable.checkVisibility({ visibilityProperty: true });
  }
  return el.getClientRects().length > 0;
};

export const getFocusable = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isVisible);

// Keydown handling for a Tab-trapping modal: Escape calls onClose; Tab and
// Shift+Tab wrap inside `root`. Pure of React so it can be tested directly.
export const trapKeydown = (
  e: Pick<KeyboardEvent, "key" | "shiftKey" | "preventDefault">,
  root: HTMLElement,
  active: Element | null,
  onClose: () => void,
): void => {
  if (e.key === "Escape") {
    onClose();
    return;
  }
  if (e.key !== "Tab") return;
  const items = getFocusable(root);
  if (items.length === 0) {
    e.preventDefault();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  if (e.shiftKey && (active === first || active === root)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  } else if (!root.contains(active)) {
    e.preventDefault();
    first.focus();
  }
};
